import type { TenantListParams } from '@/shared/api/types/tenant-types'
import { useCallback, useLayoutEffect, useMemo } from 'react'
import { useTenantLanguages, useTenantsQuery } from '@/entities/tenant'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'

export interface TenantOptionItem {
  label: string
  value: string
}

interface UseTenantListScopeOptions {
  tenantListParams?: TenantListParams
  routeTenantId?: string | undefined
}

/**
 * Derives the active tenant scope for tenant-list pages (POI, tour, survey, ...):
 * resolves currentTenantId, keeps admin-scope state in sync, and gates the
 * effective language behind the tenant's language list being fetched.
 */
export function useTenantListScope(options: UseTenantListScopeOptions = {}) {
  const { tenantListParams, routeTenantId } = options
  const { activeTenantId = '', isSuperAdmin, isTenantAdmin, setActiveTenantId } = useAdminScope()

  const { data: tenantsResponse } = useTenantsQuery(
    tenantListParams ?? { page: 1, pageSize: DEFAULT_PAGE_SIZE, isActive: true },
    { enabled: isSuperAdmin },
  )

  const tenantOptions = useMemo<TenantOptionItem[]>(() => {
    const tenants = tenantsResponse?.data ?? []
    return tenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    }))
  }, [tenantsResponse?.data])

  const currentTenantId = useMemo(() => {
    if (isTenantAdmin) return activeTenantId
    if (routeTenantId && tenantOptions.some((opt) => opt.value === routeTenantId))
      return routeTenantId
    if (activeTenantId && tenantOptions.some((opt) => opt.value === activeTenantId))
      return activeTenantId
    return tenantOptions[0]?.value ?? ''
  }, [activeTenantId, isTenantAdmin, routeTenantId, tenantOptions])

  const currentTenantName = useMemo(
    () => tenantOptions.find((item) => item.value === currentTenantId)?.label ?? '',
    [currentTenantId, tenantOptions],
  )

  useLayoutEffect(() => {
    if (isTenantAdmin || !currentTenantId || currentTenantId === activeTenantId) return
    setActiveTenantId(currentTenantId)
  }, [activeTenantId, currentTenantId, isTenantAdmin, setActiveTenantId])

  const tenantLanguagesQuery = useTenantLanguages(currentTenantId)
  const tenantLanguageItems = tenantLanguagesQuery.items
  const { selectedLanguageCode, setSelectedLanguageCode, languageOptions, defaultLanguageCode } =
    useLanguageFilter(tenantLanguageItems, currentTenantId)
  const isLanguagesReady = !currentTenantId || tenantLanguagesQuery.isFetched
  const effectiveLanguageCode = selectedLanguageCode || defaultLanguageCode

  const handleLanguageScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 40
      if (
        nearBottom &&
        tenantLanguagesQuery.hasNextPage &&
        !tenantLanguagesQuery.isFetchingNextPage
      ) {
        void tenantLanguagesQuery.fetchNextPage()
      }
    },
    [tenantLanguagesQuery],
  )

  return {
    activeTenantId,
    isSuperAdmin,
    isTenantAdmin,
    setActiveTenantId,
    tenantOptions,
    currentTenantId,
    currentTenantName,
    tenantLanguagesQuery,
    selectedLanguageCode,
    setSelectedLanguageCode,
    languageOptions,
    defaultLanguageCode,
    effectiveLanguageCode,
    isLanguagesReady,
    handleLanguageScroll,
  }
}
